import { useState, useEffect } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import qualitaLogo from "@/assets/qualita-logo-clean.png";
import qualitaLogoWhite from "@/assets/qualita-logo-white.webp";

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdown, setDropdown] = useState<string | null>(null);
  const [langDropdown, setLangDropdown] = useState(false);
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const currentLang = lang || i18n.language || "es";
  const prefix = `/${currentLang}`;

  const navItems = [
    { label: t("nav.home"), href: `${prefix}` },
    { label: t("nav.platform"), href: `${prefix}/plataforma` },
    {
      label: t("nav.solutions"),
      href: `${prefix}/soluciones`,
      children: [
        { label: t("nav.solutionsDropdown.aml"), href: `${prefix}/soluciones#aml` },
        { label: t("nav.solutionsDropdown.fraud"), href: `${prefix}/soluciones#fraud` },
        { label: t("nav.solutionsDropdown.risk"), href: `${prefix}/soluciones#risk` },
        { label: t("nav.solutionsDropdown.find"), href: `${prefix}/soluciones#find` },
        { label: t("nav.solutionsDropdown.analytics"), href: `${prefix}/soluciones#analitica` },
      ],
    },
    { label: t("nav.industries"), href: `${prefix}/industrias` },
    { label: t("nav.blog", { defaultValue: "Blog" }), href: `${prefix}/blog` },
    { label: t("nav.about"), href: `${prefix}/nosotros` },
    { label: t("nav.contact"), href: `${prefix}/contacto` },
  ];

  const isHome = location.pathname === prefix || location.pathname === `${prefix}/`;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const showTransparent = isHome && !scrolled;

  const switchLanguage = (newLang: string) => {
    const currentPath = location.pathname.replace(`/${currentLang}`, "");
    i18n.changeLanguage(newLang);
    navigate(`/${newLang}${currentPath}${location.hash}`);
    setLangDropdown(false);
  };

  const languages = [
    { code: "es", label: "ES", name: "Español" },
    { code: "en", label: "EN", name: "English" },
    { code: "pt", label: "PT", name: "Português" },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      showTransparent
        ? "bg-transparent"
        : "bg-card/90 backdrop-blur-xl border-b border-border/50 shadow-lg"
    }`}>
      <div className="container mx-auto flex items-center justify-between h-20 px-4 lg:px-8">
        <Link to={prefix} className="flex-shrink-0">
          <img
            src={showTransparent ? qualitaLogoWhite : qualitaLogo}
            alt="Qualita Corp SAS"
            width={143}
            height={40}
            className="h-10 w-auto transition-all duration-300"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.children && setDropdown(item.label)}
              onMouseLeave={() => setDropdown(null)}
            >
              <Link
                to={item.href}
                className={`text-sm font-medium transition-colors relative group flex items-center gap-1 py-2 ${
                  showTransparent
                    ? "text-primary-foreground/70 hover:text-primary-foreground"
                    : "text-foreground/70 hover:text-foreground"
                } ${location.pathname === item.href ? (showTransparent ? "text-primary-foreground" : "text-foreground") : ""}`}
              >
                {item.label}
                {item.children && <ChevronDown className="w-3 h-3" />}
                <span className={`absolute -bottom-0.5 left-0 h-0.5 transition-all duration-300 ${
                  location.pathname === item.href ? "w-full" : "w-0 group-hover:w-full"
                } ${showTransparent ? "bg-orange-cta" : "bg-accent"}`} />
              </Link>

              {item.children && dropdown === item.label && (
                <div className="absolute top-full left-0 pt-2 animate-fade-in">
                  <div className="bg-card rounded-xl border border-border shadow-xl p-2 min-w-[220px]">
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.href}
                        className="block px-4 py-2.5 text-sm text-foreground/70 hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {/* Language Switcher */}
          <div className="relative" onMouseEnter={() => setLangDropdown(true)} onMouseLeave={() => setLangDropdown(false)}>
            <button className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              showTransparent
                ? "text-primary-foreground/70 hover:text-primary-foreground"
                : "text-foreground/70 hover:text-foreground"
            }`}>
              <Globe className="w-4 h-4" />
              {currentLang.toUpperCase()}
              <ChevronDown className="w-3 h-3" />
            </button>
            {langDropdown && (
              <div className="absolute top-full right-0 pt-2 animate-fade-in">
                <div className="bg-card rounded-xl border border-border shadow-xl p-1.5 min-w-[140px]">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => switchLanguage(l.code)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${
                        currentLang === l.code
                          ? "text-foreground bg-muted font-medium"
                          : "text-foreground/70 hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      <span className="text-xs font-semibold w-6">{l.label}</span>
                      {l.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link to={`${prefix}/contacto`}>
            <Button variant="cta" size="sm" className="shadow-cta text-xs px-5">
              {t("nav.requestDemo")}
            </Button>
          </Link>
        </div>

        <div className="flex lg:hidden items-center gap-2">
          {/* Mobile language toggle */}
          <button
            onClick={() => {
              const langOrder = ["es", "en", "pt"];
              const nextIdx = (langOrder.indexOf(currentLang) + 1) % langOrder.length;
              switchLanguage(langOrder[nextIdx]);
            }}
            className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium ${
              showTransparent ? "text-primary-foreground/70" : "text-foreground/70"
            }`}
          >
            <Globe className="w-3.5 h-3.5" />
            {currentLang.toUpperCase()}
          </button>
          <button
            className={`${showTransparent ? "text-primary-foreground" : "text-foreground"}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? t("nav.closeMenu", "Cerrar menú") : t("nav.openMenu", "Abrir menú")}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-card/95 backdrop-blur-xl border-b border-border px-4 pb-4 animate-fade-in max-h-[80vh] overflow-y-auto">
          {navItems.map((item) => (
            <div key={item.label}>
              <Link
                to={item.href}
                className="block py-3 text-sm font-medium text-foreground/70 hover:text-foreground border-b border-border/30"
              >
                {item.label}
              </Link>
              {item.children && (
                <div className="pl-4">
                  {item.children.map((child) => (
                    <Link
                      key={child.label}
                      to={child.href}
                      className="block py-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <Link to={`${prefix}/contacto`} className="block mt-4">
            <Button variant="cta" className="w-full shadow-cta">{t("nav.requestDemo")}</Button>
          </Link>
        </div>
      )}
    </header>
  );
};

export default Header;
